import { useCallback, useEffect, useState } from 'react';
import {
  buildResume,
  listResumeVersions,
  type BuildResumeResponse,
  type ResumeVersion,
} from './api';

/**
 * The tailored-résumé data hook for a job (GAP 1). Ported verbatim from
 * apps/web/src/lib/useResume.ts — same shape, same house pattern as this
 * app's other per-screen hooks (useJobsInbox, useApplicationsBoard):
 * versions load on mount, `build` posts a new version and re-lists. A
 * version-list failure is non-fatal (the list is an enhancement — the
 * caller can still build a fresh one even if history fails to load).
 */
export function useResume(jobId: string | undefined) {
  const [versions, setVersions] = useState<ResumeVersion[]>([]);
  const [current, setCurrent] = useState<BuildResumeResponse | null>(null);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshVersions = useCallback(async () => {
    if (!jobId) return;
    try {
      const r = await listResumeVersions(jobId);
      setVersions(r.versions);
    } catch {
      /* non-fatal — versions list is enhancement */
    }
  }, [jobId]);

  useEffect(() => {
    refreshVersions();
  }, [refreshVersions]);

  const build = useCallback(
    async (args: { scenarioId?: string; chosenAchievementIds?: string[] }) => {
      if (!jobId) return;
      setBuilding(true);
      setError(null);
      try {
        const r = await buildResume(jobId, args);
        setCurrent(r);
        await refreshVersions();
        return r;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return undefined;
      } finally {
        setBuilding(false);
      }
    },
    [jobId, refreshVersions],
  );

  return { versions, current, building, error, build, refreshVersions, setCurrent };
}
